import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormGroup, NonNullableFormBuilder, Validators } from '@angular/forms';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import { filter, map, takeUntil } from 'rxjs/operators';
import { noop, Subject } from 'rxjs';
import {
  AssignmentCustomerResponse,
  BaseButtonSize,
  BaseSigningStatusBySide,
  BaseContractSigningTypeEnum,
  BaseCoreState,
  ContractResponse,
  BASE_PERMISSIONS,
  baseAssignmentsActions,
  baseAssignmentsSelectors,
  BaseDeregisterFormInterface,
  BaseModalService,
  BaseRolesService,
  BaseSignatureService,
  BaseSignContractOptions,
  BaseTabInterface,
} from 'base';
import { ASSIGNMENT_MENU } from '../../../../shared/_constants/assignment-menu';
import { ExternalEmployeeService } from '../../external-employee.service';
import { BaseCustomerOfferTabsEnum } from '../../../inquiry/_enums/base-customer-offer-tabs.enum';

@Component({
  selector: 'base-customer-employee-info-modal',
  templateUrl: './base-customer-employee-info-modal.component.html',
})
export class BaseCustomerEmployeeInfoModalComponent implements OnInit, OnDestroy {
  @ViewChild('deregisterModalRef') deregisterModalRef: ElementRef;
  @ViewChild('addSignSelectionPopup') addSignSelectionPopup: ElementRef;
  @ViewChild('addSignPopup') addSignPopup: ElementRef;

  @Input() modalRef: NgbModalRef;
  @Input() currentTab = ASSIGNMENT_MENU[0];
  @Output() currentTabChange = new EventEmitter<BaseTabInterface>();

  customerAssignment: AssignmentCustomerResponse;
  statusButtonBorder: string;
  updatedAvgRating: number;
  updatedRatingsAmount: number;
  tabs = [...ASSIGNMENT_MENU];
  buttonSizes = BaseButtonSize;
  signingStatusBySide = BaseSigningStatusBySide;
  contractSigningType = BaseContractSigningTypeEnum;
  validContract: ContractResponse;
  hasSignContractPermission = false;
  statuses = {
    isPlannedAssignment: false,
    isActiveAssignment: false,
    isEndedAssignment: false,
  };
  deregisterForm: FormGroup<BaseDeregisterFormInterface>;
  deregisterFormSubmitted = false;
  deregistered = false;
  addSignModalRef: NgbModalRef;
  digitalSignModalRef: NgbModalRef;
  signingType: BaseSignContractOptions | undefined;
  destroy$ = new Subject();
  customerOfferTabsEnum = BaseCustomerOfferTabsEnum;

  constructor(
    private store: Store<BaseCoreState>,
    private externalEmployeeService: ExternalEmployeeService,
    private baseRolesService: BaseRolesService,
    private fb: NonNullableFormBuilder,
    private modalService: BaseModalService,
    private baseSignatureService: BaseSignatureService
  ) {}

  get showWorkCouncilTab() {
    return (
      this.baseRolesService.hasWorkCouncilRole() ||
      this.baseRolesService.hasCustomerFeaturePermission(BASE_PERMISSIONS.FEATURE_ADMIN_WORK_COUNCIL_COMMUNICATION)
    );
  }

  ngOnInit() {
    this.hasSignContractPermission = this.baseRolesService.hasCustomerFeaturePermission(
      BASE_PERMISSIONS.FEATURE_SIGN_CONTRACT
    );

    this.store
      .select(baseAssignmentsSelectors.getCandidateReviews)
      .pipe(
        takeUntil(this.destroy$),
        filter((reviewState) => reviewState.ok === true),
        map((reviewState) => reviewState.data)
      )
      .subscribe((reviews) => {
        this.updatedAvgRating = reviews.averageRating;
        this.updatedRatingsAmount = reviews.ratings?.length;
      });

    this.store
      .select(baseAssignmentsSelectors.getAssignment)
      .pipe(
        takeUntil(this.destroy$),
        filter((state) => state.ok === true),
        map((state) => state.data)
      )
      .subscribe((assignment) => {
        this.customerAssignment = assignment;

        if (!assignment.offerId) {
          this.tabs = this.tabs.filter((tab) => tab.id !== BaseCustomerOfferTabsEnum.WorkCouncil);
        }

        this.validContract = this.customerAssignment.contracts.find((contract) => contract.valid) as ContractResponse;

        this.statuses = this.externalEmployeeService.setAssignmentTimeStatus(
          this.customerAssignment.startDate,
          this.customerAssignment.endDate
        );

        this.statusButtonBorder = this.externalEmployeeService.setSignButtonClass(
          this.validContract,
          this.hasSignContractPermission
        );
      });
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.store.dispatch(baseAssignmentsActions.clearAssignment());
    this.store.dispatch(baseAssignmentsActions.clearCustomerReviews());
    this.store.dispatch(baseAssignmentsActions.clearWorkCouncilCommentList());
  }

  showSignContractsModal(event: Event) {
    event.stopPropagation();

    this.openSignContractsModal();
  }

  closeModal() {
    this.modalRef.close();
  }

  onTabChange(tab: BaseTabInterface): void {
    this.currentTab = tab;
    this.currentTabChange.emit(tab);
  }

  openCandidateDeregisterModal(): void {
    if (!this.deregisterForm) {
      this.deregisterForm = this.fb.group({
        cancelationDate: ['', [Validators.required]],
        note: [''],
      });
    }

    const deregisterCandidateModalRef = this.modalService.open(
      {
        headText: 'externalEmployees.unsubscribe',
        bodyTemplate: this.deregisterModalRef,
        okText: 'action.yesSave',
        cancelText: 'action.cancel',
        canBeClosed: () => {
          this.deregisterFormSubmitted = true;
          return this.deregisterForm.valid;
        },
      },
      {
        size: 'lg',
      }
    );

    deregisterCandidateModalRef.result.then(
      () => {
        this.store.dispatch(
          baseAssignmentsActions.assignmentCustomerCancellation({
            payload: { assignment: this.customerAssignment.id, body: this.deregisterForm.getRawValue() },
          })
        );
        this.deregistered = true;
      },
      () => {
        deregisterCandidateModalRef.close();
      }
    );
  }

  onSignTypeChange(event: BaseSignContractOptions): void {
    this.signingType = event;
  }

  openSignContractsModal() {
    const canBeSigned = this.externalEmployeeService.canBeSigned(this.hasSignContractPermission, this.validContract);

    if (!canBeSigned) {
      return;
    }

    this.store.dispatch(baseAssignmentsActions.clearContractStatus());
    this.addSignModalRef = this.baseSignatureService.openSignContractModal(this.addSignSelectionPopup);
    this.addSignModalRef.result.then(
      () => {
        this.signingType === BaseSignContractOptions.signDigital
          ? this.openDigitalSignModal()
          : this.baseSignatureService.dispatchAssignContractAction(
              this.customerAssignment.id,
              this.validContract.id,
              this.signingType,
              true
            );
      },
      () => {
        this.signingType = undefined;
      }
    );
  }

  openDigitalSignModal() {
    this.digitalSignModalRef = this.externalEmployeeService.openDigitalSignModal(this.addSignPopup);
    this.digitalSignModalRef.result.then(() => noop, noop);
  }
}
